<template>
  <InfuseModal
    :title="t('iam.roles.import.modal.title')"
    optional-body-classes="max-w-[52rem]"
    @close="emit('close')"
  >
    <div class="flex max-h-[60vh] w-full flex-col gap-4 overflow-y-auto px-5">
      <!-- Step 1: pick the file. -->
      <template v-if="!report">
        <p class="text-sm text-neutral-800">{{ t('iam.roles.import.modal.description') }}</p>

        <form id="import-roles" @submit.prevent="onPreview">
          <InfuseField :label="t('iam.roles.import.modal.fileLabel')" size="small">
            <InfuseFileInput accept=".json,application/json" v-model="selectedFile" :required="true" />
          </InfuseField>
        </form>

        <InfuseNote v-if="parseError" type="error" data-testid="import-roles-parse-error">
          {{ parseError }}
        </InfuseNote>
      </template>

      <!-- Step 2: review what it would do, before anything is changed. -->
      <template v-else>
        <p class="text-sm text-neutral-800">{{ t('iam.roles.import.modal.reviewDescription') }}</p>
        <RoleImportPreview :report="report" />
      </template>
    </div>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="onCancel">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton
        v-if="!report"
        data-testid="modal-preview-button"
        type="submit"
        form="import-roles"
        :disabled="!selectedFile || isPreviewing"
        :loading="isPreviewing"
      >
        {{ t('iam.roles.import.modal.review') }}
      </InfuseButton>

      <InfuseButton
        v-else
        data-testid="modal-confirm-button"
        :disabled="nothingToApply || isImporting"
        :loading="isImporting"
        @click="onConfirm"
      >
        {{ t('iam.roles.import.modal.apply') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { RoleImportReport, RoleTransfer } from '@infuse/iam/role/type';
import { InfuseButton, InfuseField, InfuseFileInput, InfuseModal, InfuseNote } from '@infuse/ui/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RoleImportPreview from './RoleImportPreview.vue';

const { t } = useI18n();

const props = defineProps<{
  /** Restricts the file to a single role, for the import action on a role's own page. */
  singleRoleOnly?: boolean;
  isPreviewing?: boolean;
  isImporting?: boolean;
  /** Set by the parent once the preview comes back; switches the modal to the review step. */
  report?: RoleImportReport;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'preview', roles: RoleTransfer[]): void;
  (e: 'confirm', roles: RoleTransfer[]): void;
}>();

const selectedFile = ref<File>();
const parseError = ref<string>();
const parsedRoles = ref<RoleTransfer[]>([]);

const nothingToApply = computed(
  () => !props.report || (props.report.summary.toCreate === 0 && props.report.summary.toUpdate === 0)
);

async function onPreview(): Promise<void> {
  parseError.value = undefined;

  if (!selectedFile.value) {
    return;
  }

  const roles = await parseFile(selectedFile.value);
  if (!roles) {
    return;
  }

  parsedRoles.value = roles;
  emit('preview', roles);
}

/**
 * Checks the file is the right shape here rather than letting the server reject it, so that picking
 * the wrong file gives an immediate, obvious answer.
 */
async function parseFile(file: File): Promise<RoleTransfer[] | undefined> {
  let parsed: unknown;

  try {
    parsed = JSON.parse(await file.text());
  } catch {
    parseError.value = t('iam.roles.import.error.notJson');
    return undefined;
  }

  if (!Array.isArray(parsed)) {
    parseError.value = t('iam.roles.import.error.notAnArray');
    return undefined;
  }

  if (parsed.length === 0) {
    parseError.value = t('iam.roles.import.error.empty');
    return undefined;
  }

  if (props.singleRoleOnly && parsed.length > 1) {
    parseError.value = t('iam.roles.import.error.singleRoleOnly', { count: parsed.length });
    return undefined;
  }

  if (!parsed.every(isRoleTransfer)) {
    parseError.value = t('iam.roles.import.error.wrongShape');
    return undefined;
  }

  return parsed as RoleTransfer[];
}

function isRoleTransfer(value: unknown): boolean {
  const role = value as Partial<RoleTransfer>;

  return (
    !!role &&
    typeof role.name === 'string' &&
    !!role.scope &&
    typeof role.scope.level === 'string' &&
    Array.isArray(role.permissions)
  );
}

function onConfirm(): void {
  emit('confirm', parsedRoles.value);
}

function onCancel(): void {
  emit('close');
}
</script>
